// Push Notification Service for Web Push API

const API = process.env.REACT_APP_BACKEND_URL;

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  // Check if push notifications are supported
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Check if notifications are currently enabled
  isEnabled() {
    return Notification.permission === 'granted';
  }

  // Check if notifications are denied
  isDenied() {
    return Notification.permission === 'denied';
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Push notifications not supported');
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // Register service worker and get push subscription
  async subscribe() {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    // Request permission first
    const granted = await this.requestPermission();
    if (!granted) {
      throw new Error('Notification permission denied');
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const response = await fetch(`${API}/api/push/vapid-public-key`);
      const { publicKey } = await response.json();

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(publicKey);

      // Subscribe to push
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to server
      await fetch(`${API}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: this.subscription.endpoint,
          keys: {
            p256dh: this.arrayBufferToBase64(this.subscription.getKey('p256dh')),
            auth: this.arrayBufferToBase64(this.subscription.getKey('auth'))
          }
        }),
        credentials: 'include'
      });

      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();
      }

      // Notify server
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        credentials: 'include'
      });

      this.subscription = null;
      console.log('Push unsubscription successful');
      return true;
    } catch (error) {
      console.error('Push unsubscription failed:', error);
      throw error;
    }
  }

  // Convert URL-safe base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Convert ArrayBuffer to base64
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// Singleton instance
const pushService = new PushNotificationService();
export default pushService;
